import React from 'react';
import styled from 'styled-components';
import { animated } from '@react-spring/web';
import { useSelector } from 'react-redux';
import { getSelectedGroupId, selectGroup } from 'ducks/products';
import ProductMenuContent from './ProductMenuContent';
import Button from 'ui/Button/Button';
import ChevronLeftIcon from '@ingka/ssr-icon/paths/chevron-left';
import { ButtonTypeEnum } from 'ui/Button/Button';
import IconButtonSizeEnum from '@inter-ikea-kompis/component-icon-button/lib/enums/IconButtonSizeEnum';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { usePlannerConfig } from 'contexts/plannerConfig';
import * as R from 'ramda';
import Title from 'ui/Typography/Title';
import FilterButton from 'components/FiltersButton/FilterButton';
import { setActiveWidget, showMobileFiltersSheet } from 'ducks/mobile';
import { useAppDispatch } from 'ducks/hooks';
import { useHistoryEventTrigger } from 'components/History/History';
import ErrorMessageToast from 'rangeConfig/shared/widgets/ErrorMessageToast';
import { useBehaviorTracker } from 'contexts/behaviors';

const MenuContainer = styled(animated.div)`
  position: fixed;
  transition: transform 0.1s ease-out;
  width: 100%;
  background: white;
  display: grid;
  grid-template-rows: 53px auto;
  z-index: 100;
`;

const OutletHeader = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.grey[100]};
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const TitleAndIconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  display: flex;
  align-items: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

type Props = {
  animationProps: any;
  height: number;
};

const ProductMenu = ({ animationProps, height }: Props) => {
  const { formatMessage } = useIntl();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const behaviorTracker = useBehaviorTracker();
  useHistoryEventTrigger();

  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);
  const dispatch = useAppDispatch();

  const onBackClicked = () => {
    if (selectedGroupId) {
      behaviorTracker
        .withContext({ groupId: selectedGroupId, menuType: 'main' })
        .trigger('productGroupExited', null);
    }
    dispatch(setActiveWidget(null));
    dispatch(selectGroup(''));
  };

  const hasFilter = selectedGroup?.mainMenu.filters;

  const hideHeader = productGroups.length === 1; // Should not have back button if only one group

  return (
    <MenuContainer
      style={{
        transform: animationProps?.menuHeight.to(
          (menuHeight: number) => `translateY(-${menuHeight}px)`
        ),
        height: `${height}px`,
        bottom: `${-height}px`
      }}
    >
      <OutletHeader>
        <TitleAndIconWrapper>
          {!hideHeader && (
            <Button
              iconOnly
              size={IconButtonSizeEnum.medium}
              type={ButtonTypeEnum.tertiary}
              onClick={onBackClicked}
              ssrIcon={ChevronLeftIcon}
              dataTestId="mobile-menu-back-btn"
              aria-label={formatMessage({ id: 'storageone.back' })}
            />
          )}
          {selectedGroup && (
            <StyledTitle type={ThemeFontStyleTypeEnum.headingSmall} ml={hideHeader ? 's' : 0}>
              {formatMessage({ id: selectedGroup?.mainMenu.menuTitle })}
            </StyledTitle>
          )}
        </TitleAndIconWrapper>
        {hasFilter && <FilterButton onClick={() => dispatch(showMobileFiltersSheet(true))} />}
      </OutletHeader>
      <ContentWrapper>
        <ErrorMessageToast />
        <ProductMenuContent />
      </ContentWrapper>
    </MenuContainer>
  );
};

export default ProductMenu;
